import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from '@/lib/toast';

export const REPORT_TYPES = [
  { value: 'counterfeit', label: 'Counterfeit/Fake Item' },
  { value: 'fraud', label: 'Fraud/Scam Attempt' },
  { value: 'prohibited', label: 'Prohibited Item' },
  { value: 'stolen', label: 'Suspected Stolen Property' },
  { value: 'harassment', label: 'Harassment/Abuse' },
  { value: 'spam', label: 'Spam/Misleading Listing' },
  { value: 'other', label: 'Other' },
] as const;

export type ReportType = typeof REPORT_TYPES[number]['value'];

interface SubmitReportData {
  reportedUserId?: string;
  reportedItemId?: string;
  type: ReportType;
  description: string;
}

export function useSubmitReport() {
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (data: SubmitReportData) => {
      if (!user) {
        throw new Error('You must be logged in to submit a report');
      }

      if (!data.reportedUserId && !data.reportedItemId) {
        throw new Error('Must provide either a user or item to report');
      }

      const { error } = await supabase.from('reports').insert({
        reporter_id: user.id,
        reported_user_id: data.reportedUserId || null,
        reported_item_id: data.reportedItemId || null,
        type: data.type,
        description: data.description,
        status: 'pending',
      });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Report submitted', {
        description: 'Thank you for helping keep our community safe.',
      });
    },
    onError: (error) => {
      console.error('Failed to submit report:', error);
      toast.error('Failed to submit report', {
        description: error instanceof Error ? error.message : 'Please try again later.',
      });
    },
  });
}
